import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Edit3, Save, X, Layers } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const empty = { name: "", description: "", weight: 10, order: 0, is_pre_diagnosis: false };

const AdminSections = () => {
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => {
    setLoading(true);
    api
      .get("/sections")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, order: items.length });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name,
      description: it.description || "",
      weight: it.weight,
      order: it.order,
      is_pre_diagnosis: !!it.is_pre_diagnosis,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Informe o nome da sessão.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        weight: Number(form.weight) || 0,
        order: Number(form.order) || 0,
        is_pre_diagnosis: !!form.is_pre_diagnosis,
      };
      if (editing) {
        await api.put(`/sections/${editing.id}`, payload);
        toast.success("Sessão atualizada.");
      } else {
        await api.post("/sections", payload);
        toast.success("Sessão criada.");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Excluir a sessão "${it.name}"?\nTodas as perguntas vinculadas também serão removidas.`)) return;
    try {
      await api.delete(`/sections/${it.id}`);
      toast.success("Sessão excluída.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totalWeight = items.reduce((acc, s) => acc + (Number(s.weight) || 0), 0);

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3]">
            Configuração
          </div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Layers className="text-[#B9262B]" /> Sessões
          </h1>
          <p className="text-[#9aa3ad] mt-1">
            Cada sessão agrupa perguntas e possui um peso (0–100%).{" "}
            <span className="text-white">Soma atual: {totalWeight}%</span>
          </p>
        </div>
        {canEdit && (
          <Button
            data-testid="new-section-btn"
            onClick={openNew}
            className="bg-[#B9262B] hover:bg-[#9a1e22] text-white"
          >
            <Plus size={16} className="mr-2" /> Nova sessão
          </Button>
        )}
      </div>

      <div className="border border-[#3A3F47] rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#6B8DB3]">
            <Loader2 className="animate-spin inline mr-2" /> Carregando...
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-[#6B8DB3]">
            Nenhuma sessão cadastrada ainda.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#0e1015] text-[#6B8DB3] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-4 w-16">Ord.</th>
                <th className="text-left p-4">Nome</th>
                <th className="text-left p-4 hidden md:table-cell">Descrição</th>
                <th className="text-left p-4 w-24">Peso</th>
                <th className="text-right p-4 w-44"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it.id}
                  className="border-t border-[#1C1F26] hover:bg-[#1C1F26]/40"
                  data-testid={`section-row-${it.id}`}
                >
                  <td className="p-4 font-mono text-[#6B8DB3]">{it.order}</td>
                  <td className="p-4 text-white font-medium">
                    {it.name}
                    {it.is_pre_diagnosis && (
                      <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#22B673]/15 text-[#22B673] border border-[#22B673]/40 align-middle">
                        Pré-diagnóstico
                      </span>
                    )}
                  </td>
                  <td className="p-4 text-[#9aa3ad] hidden md:table-cell">
                    <div className="line-clamp-1 max-w-md">{it.description}</div>
                  </td>
                  <td className="p-4 font-mono text-[#B9262B]">{it.weight}%</td>
                  <td className="p-4 text-right">
                    {canEdit ? (
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => openEdit(it)}
                          data-testid={`edit-section-${it.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs bg-[#1C1F26] hover:bg-[#252932] border border-[#3A3F47]"
                        >
                          <Edit3 size={14} /> Editar
                        </button>
                        <button
                          onClick={() => remove(it)}
                          data-testid={`delete-section-${it.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs bg-[#B9262B]/15 hover:bg-[#B9262B]/30 text-[#B9262B] border border-[#B9262B]/40"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider text-[#6B8DB3]">
                        Somente leitura
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1C1F26] border-[#3A3F47] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar sessão" : "Nova sessão"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">
                Nome
              </Label>
              <Input
                data-testid="section-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">
                Descrição
              </Label>
              <Textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">
                  Peso (%)
                </Label>
                <Input
                  data-testid="section-weight-input"
                  type="number"
                  min={0}
                  max={100}
                  value={form.weight}
                  onChange={(e) => setForm({ ...form, weight: e.target.value })}
                  className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">
                  Ordem
                </Label>
                <Input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: e.target.value })}
                  className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
                />
              </div>
            </div>
            <label
              htmlFor="is-pre-diag"
              className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                form.is_pre_diagnosis
                  ? "border-[#22B673] bg-[#22B673]/5"
                  : "border-[#3A3F47] bg-[#0e1015] hover:border-[#6B8DB3]/50"
              }`}
            >
              <input
                id="is-pre-diag"
                type="checkbox"
                data-testid="section-pre-diag-toggle"
                checked={!!form.is_pre_diagnosis}
                onChange={(e) => setForm({ ...form, is_pre_diagnosis: e.target.checked })}
                className="mt-1 w-4 h-4 accent-[#22B673]"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-white">
                  Esta seção faz parte do pré-diagnóstico?
                </div>
                <div className="text-xs text-[#9aa3ad] mt-1 leading-relaxed">
                  Seções marcadas como pré-diagnóstico são exibidas <strong>antes</strong> das demais.
                  Após responder a última, o respondente vê um resultado resumido e decide se continua
                  o formulário ou encerra naquele momento.
                </div>
              </div>
            </label>
          </div>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-[#3A3F47] text-white hover:bg-[#0e1015]"
            >
              <X size={14} className="mr-2" /> Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="save-section-btn"
              className="bg-[#B9262B] hover:bg-[#9a1e22] text-white"
            >
              {saving ? (
                <Loader2 className="animate-spin" />
              ) : (
                <>
                  <Save size={14} className="mr-2" /> Salvar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSections;
